/**
 * Outbound settlement vocabulary — what happened when a send tried to settle its row.
 *
 * Its own module, dependency-free, for the reason given in `block-reasons.js`: a vocabulary
 * is read by things that have no business loading the machinery that produces it. Two
 * concrete consumers force it here:
 *
 *   - `schemas.js` must stay dependency-free, so it cannot import `events.js` (which builds
 *     AWS clients at load). It therefore carried a hand-copied duplicate of the DELIVERY_PROOF
 *     enum. Both now read this module, so the ladder has one definition again.
 *   - The ten `outbound_*` verdicts were inline string literals in `delivery-handler` alone.
 *     Nothing else could name them, so every other handler invented its own answer to the
 *     same physical state.
 *
 * `events.js` re-exports DELIVERY_PROOF and DELIVERY_PROOF_RANK, so existing importers are
 * unchanged.
 *
 * @module outbound-settlement-vocabulary
 */

'use strict';

/**
 * DELIVERY EVIDENCE GRADES — "was this person reached, and how well do we know?"
 *
 * Delivery evidence is unevenly available per channel, so the grade travels with the
 * answer. Pretending a socket ack is the same as a carrier receipt is exactly how
 * `status: executed` — stamped before anything is sent — came to be read as delivery.
 * The full definitions live on `DecisionSchema.deliveryProof` in ./schemas.js.
 */
const DELIVERY_PROOF = Object.freeze({
  NONE: 'none',
  ACCEPTED: 'accepted',
  RENDERED: 'rendered',
  CONFIRMED: 'confirmed',
  REFUTED: 'refuted'
});

/**
 * How strong each grade is. A stamp may only ever move the grade UP this ladder:
 * a generous grade is worse than no grade, because governance will trust it.
 *
 * `refuted` sits at the top because it is the only grade that RETRACTS — positive
 * evidence that a message did not land outranks any earlier optimism about it, and
 * nothing weaker may overwrite it.
 */
const DELIVERY_PROOF_RANK = Object.freeze({
  [DELIVERY_PROOF.NONE]: 0,
  [DELIVERY_PROOF.ACCEPTED]: 1,
  [DELIVERY_PROOF.RENDERED]: 2,
  [DELIVERY_PROOF.CONFIRMED]: 3,
  [DELIVERY_PROOF.REFUTED]: 4
});

/** Every grade, in ladder order — for `z.enum` and for tests that must cover all of them. */
const DELIVERY_PROOF_VALUES = Object.freeze([
  DELIVERY_PROOF.NONE,
  DELIVERY_PROOF.ACCEPTED,
  DELIVERY_PROOF.RENDERED,
  DELIVERY_PROOF.CONFIRMED,
  DELIVERY_PROOF.REFUTED
]);

/**
 * What settling one outbound row against a transport event concluded.
 *
 * ⚠️ EVERY VALUE CARRIES A VERDICT ON DURABILITY, and the two kinds must never look alike:
 * `UNRESOLVED` means the evidence is recorded NOWHERE and the handler must fail so the
 * event is retried. Everything else is terminal — the question is answered and retrying
 * it will not change the answer.
 *
 * `ROW_ABSENT` is the value that did not exist before. "The row is not there" used to be
 * folded in with "the read failed" under UNRESOLVED, so a send that never had a row — a
 * legacy `recovery-emails` upsert, a rowless test send — paged on every delivery
 * confirmation, while a genuine DynamoDB failure paged for the same reason. They need
 * different answers: absence is terminal after the bounded retries, failure is not.
 */
const SETTLEMENT_ACTIONS = Object.freeze({
  // Terminal — nothing to settle, and that is correct.
  NO_CORRELATION_TAGS: 'no_outbound_correlation_tags',
  MERCHANT_TEST_DELIVERED: 'merchant_test_delivered',
  MISSING_WORKSPACE_TAG: 'missing_workspace_tag',
  NO_DECISION_TAG: 'no_decision_tag',

  // Terminal — the row exists but this event must not change it.
  IDENTITY_MISMATCH: 'outbound_identity_mismatch',
  SES_MESSAGE_MISMATCH: 'outbound_ses_message_mismatch',
  DECISION_MISMATCH: 'outbound_decision_mismatch',
  STRONGER_PROOF_HELD: 'outbound_stronger_proof_held',

  // Terminal — the row is genuinely not there after the bounded retries.
  ROW_ABSENT: 'outbound_row_absent',

  // Terminal — the row exists and keeps moving. Every attempt's conditional write lost the
  // race to a concurrent writer (the reply sender's own `sending` → `sent` update is the
  // realistic one). "The condition lost" and "the write failed" are different facts: the
  // first means somebody else settled the row, which is not evidence loss and must not page.
  CONCURRENT_UPDATE: 'outbound_concurrent_update',

  // Terminal — settled.
  DELIVERY_CONFIRMED: 'outbound_delivery_confirmed',
  DELIVERY_ALREADY_CONFIRMED: 'outbound_delivery_already_confirmed',
  BOUNCE_REFUTED: 'outbound_bounce_refuted',
  BOUNCE_ALREADY_REFUTED: 'outbound_bounce_already_refuted',

  // NOT terminal: nothing recorded this evidence, so it has to be retried.
  //
  // ⚠️ "Retried" means the CALLER decides, and the answer is not the same everywhere. On a
  // decisionless reply the outbound row is the whole receipt, so `delivery-handler` throws
  // and lets SNS redeliver. On a decision-backed send the decision proof and the
  // `email_delivered` event have already landed by that point, so it counts
  // `OutboundSettlementUnresolvedWithDecisionProof` and returns instead — throwing there
  // would page the DLQ over a subordinate receipt and re-stamp proof that already exists.
  // This value says "recorded nowhere"; it does not say "throw".
  UNRESOLVED: 'outbound_delivery_unresolved'
});

/**
 * The only action that means "recorded nowhere". Everything else is an answer.
 *
 * How a caller ACTS on that is the caller's call — see the note on UNRESOLVED above.
 * Kept as a set rather than a `!== UNRESOLVED` check so a new non-durable verdict has
 * exactly one place to be declared.
 */
const UNRESOLVED_SETTLEMENT_ACTIONS = Object.freeze(new Set([
  SETTLEMENT_ACTIONS.UNRESOLVED
]));

/** True when the verdict means the evidence is recorded nowhere and the event must be retried. */
function isUnresolvedSettlement(action) {
  return UNRESOLVED_SETTLEMENT_ACTIONS.has(action);
}

/**
 * Validate that an SES event and an outbound email row describe the same physical send.
 * Missing optional correlation is tolerated for deploy-boundary compatibility; two present
 * values that disagree are positive evidence of crossed records and are terminal.
 */
function outboundCorrelationMismatch(row, { workspaceId, emailId, decisionId, sesMessageId }) {
  if (row.workspaceId !== workspaceId || row.messageId !== emailId || row.channel !== 'email') {
    return SETTLEMENT_ACTIONS.IDENTITY_MISMATCH;
  }
  if (row.sesMessageId && row.sesMessageId !== sesMessageId) {
    return SETTLEMENT_ACTIONS.SES_MESSAGE_MISMATCH;
  }
  if (row.transportMessageId && row.transportMessageId !== sesMessageId) {
    return SETTLEMENT_ACTIONS.SES_MESSAGE_MISMATCH;
  }
  if (row.decisionId && decisionId && row.decisionId !== decisionId) {
    return SETTLEMENT_ACTIONS.DECISION_MISMATCH;
  }
  return null;
}

module.exports = {
  DELIVERY_PROOF,
  DELIVERY_PROOF_RANK,
  DELIVERY_PROOF_VALUES,
  SETTLEMENT_ACTIONS,
  UNRESOLVED_SETTLEMENT_ACTIONS,
  isUnresolvedSettlement,
  outboundCorrelationMismatch
};
